import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, RefreshCw, Clock, CheckCircle, XCircle } from 'lucide-react';
import { swingLabRuns } from '../data/runs';
import type { SwingRunSummary } from '../types';
import { formatDistanceToNow } from 'date-fns';

interface RecentRunsListProps {
  videoAssetId: string;
  onReloadRun?: (run: SwingRunSummary) => void;
}

export function RecentRunsList({ videoAssetId, onReloadRun }: RecentRunsListProps) {
  const [runs, setRuns] = useState<SwingRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRuns = async () => {
    try {
      setLoading(true);
      const recentRuns = await swingLabRuns.listRunsForAsset(videoAssetId, 5);
      setRuns(recentRuns);
      setError(null);
    } catch (err) {
      console.error('Failed to load runs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (videoAssetId) {
      loadRuns();
    }
  }, [videoAssetId]);

  const handleExportRun = async (run: SwingRunSummary) => {
    try {
      // Create and download summary JSON
      const summary = {
        id: run.id,
        videoAssetId: run.videoAssetId,
        mode: run.mode,
        status: run.status,
        processedFrames: run.processedFrames,
        drawnFrames: run.drawnFrames,
        metrics: run.metrics,
        createdAt: run.createdAt,
        completedAt: run.completedAt
      };

      const blob = new Blob([JSON.stringify(summary, null, 2)], {
        type: 'application/json'
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `swing-analysis-${run.id.slice(0, 8)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export run:', err);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      completed: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      started: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };

    return (
      <Badge className={colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200'}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Recent Runs</h3>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <h3 className="font-semibold text-red-900 mb-2">Failed to Load Runs</h3>
        <p className="text-sm text-red-700 mb-4">{error}</p>
        <Button variant="outline" size="sm" onClick={loadRuns}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900">Recent Runs</h3>
        <Button variant="outline" size="sm" onClick={loadRuns}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {runs.length === 0 ? (
        <div className="text-center py-8">
          <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No analysis runs yet</p>
          <p className="text-sm text-slate-400">Run an analysis to see results here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {runs.map((run) => (
            <div
              key={run.id}
              className="flex items-center gap-3 p-3 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <div className="flex-shrink-0">
                {getStatusIcon(run.status)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {getStatusBadge(run.status)}
                  <span className="text-sm text-slate-600">
                    {run.processedFrames} frames
                  </span>
                  <span className="text-sm text-slate-600">
                    {run.detectCadence}:1 cadence
                  </span>
                </div>
                <div className="text-xs text-slate-500">
                  {formatDistanceToNow(new Date(run.createdAt), { addSuffix: true })}
                  {run.completedAt && (
                    <span className="ml-2">
                      • Duration: {Math.round((new Date(run.completedAt).getTime() - new Date(run.createdAt).getTime()) / 1000)}s
                    </span>
                  )}
                  {run.firstResultAtMs && (
                    <span className="ml-2">
                      • First result: {run.firstResultAtMs}ms
                    </span>
                  )}
                </div>
                {run.error && (
                  <div className="text-xs text-red-600 mt-1 truncate">
                    Error: {run.error}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1">
                {run.status === 'completed' && onReloadRun && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onReloadRun(run)}
                    title="Reload overlay from saved data"
                  >
                    Open
                  </Button>
                )}
                {run.status === 'completed' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleExportRun(run)}
                    title="Export summary JSON"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}